<template>
  <div class="gr-15 gr-sm-12 d-flex flex-column">
    <div
      v-for="group in groups"
      :key="group.id + 'group'"
    >
      <h5 class="text-h5">
        <template v-if="isGrandPrixPage">
          Лучший {{ uncapitalize(group.speciality_name) }}
        </template>
        <template v-else>
          {{ group.speciality_name }}
        </template>
      </h5>

      <div class="d-flex flex-wrap flex-1-1 mx-n2 mb-n2 mt-4">
        <v-col
          v-for="(item, idx) in group.doctors"
          :key="item.id + 'item'"
          cols="12"
          md="4"
        >
          <v-hover>
            <template #default="{ isHovering, props }">
              <div
                v-bind="props"
                class="pa-4 cursor-pointer rounded-lg"
                :class="[
                  isDark && isHovering ? 'bg-ui-kit-bg-gray-20' : 'bg-transparent',
                  !isDark && isHovering ? 'elevation-2' : '',
                ]"
                @click="emit('item:click', item, group.speciality_name, idx + 1)"
              >
                <div class="winner-item d-flex align-start">
                  <div
                    class="bg-white rounded d-inline-block flex-grow-0"
                  >
                    <v-img
                      v-if="isClient"
                      :key="item.image || DOCTOR_IMG_PLACEHOLDER"
                      class="rounded winner-item__img"
                      :class="{ 'winner-item__img_outlined': !isGrandPrixPage }"
                      :width="96"
                      :height="96"
                      :src="!item.image ? DOCTOR_IMG_PLACEHOLDER : `${$config.awardUrl}${item.image}`"
                      :alt="`${item.surname} ${item.firstname} ${item.secondname}
                        - ${idx + 1} место по специальности ${group.speciality_name.toLowerCase()}`"
                      :lazy-src="DOCTOR_IMG_PLACEHOLDER"
                      :eager="false"
                    />
                    <img
                      v-else
                      width="96"
                      height="96"
                      class="rounded winner-item__img"
                      alt="Врач"
                      :class="{ 'winner-item__img_outlined': !isGrandPrixPage }"
                      :src="DOCTOR_IMG_PLACEHOLDER"
                    />
                  </div>
                  <div class="ml-4">
                    <h4
                      class="text-body-1"
                      :class="{
                        'text-primary': isHovering,
                      }"
                    >
                      <span>
                        {{ item.surname }} <br />
                        {{ item.firstname }} <br />
                        {{ item.secondname }} <br />
                      </span>
                    </h4>
                    <div class="mt-2">
                      <div
                        v-if="isGrandPrixPage"
                        class="d-flex align-center"
                      >
                        <v-img
                          v-if="idx === 0"
                          class="mr-2"
                          width="32"
                          height="30"
                          max-width="32"
                          max-height="30"
                          alt=""
                          src="/graphics/award-star-gold.svg"
                        />

                        <span
                          class="text-subtitle-1"
                          :class="{'text-primary': idx === 0}"
                        >
                          {{ idx === 0 ? 'Победитель' : 'Номинант' }}
                        </span>
                      </div>
                      <div
                        v-else
                        class="d-flex"
                      >
                        <v-img
                          v-if="AWARD_ICONS[idx + 1 as keyof typeof AWARD_ICONS]"
                          class="mr-2"
                          width="20"
                          height="20"
                          max-width="20"
                          max-height="20"
                          :src="AWARD_ICONS[idx + 1 as keyof typeof AWARD_ICONS]"
                        />

                        <span class="text-subtitle-1">
                          {{ idx + 1 }} место
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </v-hover>
        </v-col>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import { useTheme } from 'vuetify'
import type { IDoctor, IDoctorGroup } from '~/types'
import { DOCTOR_IMG_PLACEHOLDER } from '~/constants'
import { AWARD_ICONS } from '~/features/doctors/data/award-icons'

defineProps({
  groups: { type: Array as PropType<IDoctorGroup[]>, default: () => [] },
})

const emit = defineEmits<{
  'item:click': [_doctor: IDoctor, _speciality: IDoctorGroup['speciality_name'], _place: number]
}>()

const { isGrandPrixPage } = usePageType()

const theme = useTheme()

const isDark = computed(() => theme.current.value.dark)

function uncapitalize(string: string) {
  if (string && string.length > 1 && /[а-яё]/.test(string[1])) {
    return string[0].toLowerCase() + string.slice(1)
  }

  return string
}

const $config = useRuntimeConfig().public

// для оптимизации SSR
const isClient = import.meta.client
</script>

<style lang="scss" scoped>
.winner-item {
  &__img_outlined {
    border: 1px solid $t-ui-kit-bg-gray-60;
  }
}
</style>
